import { usableDraft } from '../lib/booking-draft'
import { clearAttempt } from '../lib/payer-email'
import { sameDayOpen } from '../lib/same-day'
import { slots } from './constants/slots'

const STORAGE_KEY = 'gwmc_booking_state'

// We write the booking state to BOTH sessionStorage and localStorage before
// the Stripe redirect. sessionStorage is the preferred source on return (it's
// scoped to the tab and clears on close), but iOS Safari evicts it under
// memory pressure / tab backgrounding - so localStorage is the fallback that
// survives the round-trip. localStorage entries are read only if recent and
// always cleared once consumed so they can't leak across unrelated visits.
//
// Book & pay also writes here on every change (`form` and `step: 'book'`),
// so a reload or a return in the same browser keeps the whole form
// (lib/booking-draft.js). That draft is kept for a day and cleared once the
// booking is paid.

const safeSet = (storage, value) => {
  try {
    storage.setItem(STORAGE_KEY, value)
  } catch (e) {
    // Quota / private mode - ignore, the other layer may still succeed
  }
}

const safeGet = storage => {
  try {
    return storage.getItem(STORAGE_KEY)
  } catch (e) {
    return null
  }
}

const safeRemove = storage => {
  try {
    storage.removeItem(STORAGE_KEY)
  } catch (e) {
    // ignore
  }
}

// The stored draft as it is, or null.
const readRaw = () => {
  try {
    const raw = safeGet(window.sessionStorage) || safeGet(window.localStorage)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

// `extra`: { step, form } from Book & pay. A save without it (the Stripe
// redirect in use-orders-api) keeps the step and form already written, so
// coming back from Stripe returns to Book & pay with the form as it was.
export const saveBookingState = (state, extra = null) => {
  try {
    const previous = extra ? null : readRaw()
    const bookingData = {
      step: extra?.step ?? previous?.step ?? null,
      form: extra?.form ?? previous?.form ?? null,
      // Set by the save just before the Stripe redirect (no `extra`), so the
      // return to Book & pay can be counted (stripe_checkout_returned).
      redirectedAt: extra ? null : Date.now(),
      services: state.services,
      servicesAvailability: state.servicesAvailability,
      address: state.address,
      postcode: state.postcode,
      homeAddress: state.homeAddress,
      date: state.date,
      timeSlot: state.timeSlot,
      carModel: state.carModel,
      carColor: state.carColor,
      carRegistration: state.carRegistration,
      notes: state.notes,
      agentTip: state.agentTip,
      promoCode: state.promoCode,
      savedAt: Date.now()
    }
    const serialised = JSON.stringify(bookingData)
    safeSet(window.sessionStorage, serialised)
    safeSet(window.localStorage, serialised)
  } catch (e) {
    console.warn('[BookingPersistence] Failed to save:', e)
  }
}

// A whole saved state as it comes (the unfinished-booking resume link,
// lib/booking-capture.js restoreToSaved), written as saveBookingState
// writes, so the next load of Book & pay puts it back.
export const saveRawBookingState = data => {
  try {
    const serialised = JSON.stringify(data)
    safeSet(window.sessionStorage, serialised)
    safeSet(window.localStorage, serialised)
  } catch (e) {
    console.warn('[BookingPersistence] Failed to save:', e)
  }
}

export const loadBookingState = () => {
  try {
    // Prefer sessionStorage; fall back to localStorage when iOS Safari has
    // evicted the tab-scoped copy after a background → Stripe → return trip.
    const raw = safeGet(window.sessionStorage) || safeGet(window.localStorage)
    if (!raw) return null

    const now = new Date()
    const data = usableDraft(JSON.parse(raw), { now, todayOpen: sameDayOpen(now), sameDaySlot: slots.today })
    if (!data) {
      clearBookingState()
      return null
    }
    if (data.form) data.form = usableDraft({ ...data.form, savedAt: data.savedAt, step: data.step }, { now, todayOpen: sameDayOpen(now), sameDaySlot: slots.today })

    return data
  } catch (e) {
    console.warn('[BookingPersistence] Failed to load:', e)
    return null
  }
}

// Paid (or abandoned for good): the form, and the card path's attempt (its
// key, guest and typed email, lib/payer-email.js), both go.
export const clearBookingState = () => {
  safeRemove(window.sessionStorage)
  safeRemove(window.localStorage)
  clearAttempt()
}
